// =====================================================================
// src/lib/v2/openStatusOverride.js
//
// Single source of truth for "is this business open right now?" across
// all customer-facing surfaces (ConnectList, BusinessProfile,
// BusinessDirectory, Cart, HomeConnectListV2). Wraps the existing
// hours-based `openStatus()` with the merchant's manual override:
//
//   business.manualOpenOverride: {
//     date: 'YYYY-MM-DD',   // the local day the override was set on
//     open: boolean,         // explicit yes/no
//     setAt: Timestamp,
//   }
//
// Semantics:
//   - No override OR override.date is not today  → fall back to hours
//   - override.date === today && override.open === false → force closed
//   - override.date === today && override.open === true  → force open
//     (preserves the closing-time string from hours WHEN hours also
//      currently say open, so the pill stays informative; otherwise
//      generic "Open today")
//
// Date-stamping means stale overrides auto-expire next day — no daily
// cleanup needed. Merchant flipped the toggle yesterday? Yesterday's
// override is silently ignored today; the next merchant action writes
// a fresh date.
//
// Why a wrapper file vs editing openStatus directly: openStatus lives in
// v2/firestore/derive.js alongside dozens of other helpers. Keeping the
// override logic in its own file makes the change reviewable and easy
// to walk back if we ever decide the merchant override shouldn't
// propagate (e.g. for the directory but not the connect list).
// =====================================================================

import { openStatus } from './firestore';

export function getEffectiveOpenStatus(biz) {
  // Defensive default — caller should never pass falsy, but if they do
  // we don't want the whole render to throw.
  if (!biz) return { open: false, openMeta: '' };

  // Local-time YYYY-MM-DD. toISOString() returns UTC, so for an IST
  // user just past midnight it could disagree with the user's perceived
  // "today". Override mismatch only causes one day of stale display
  // around the date boundary; acceptable for V2.0. If this turns out to
  // bite people we'd switch to a tz-aware library.
  const todayStr = new Date().toISOString().split('T')[0];

  const override = biz.manualOpenOverride;
  if (override && override.date === todayStr) {
    if (override.open) {
      // Force-open. We want to keep the meta string informative when
      // possible — show "Open till 9 PM" if hours ALSO currently say
      // open. But if hours say closed (e.g. it's 6 AM and the business
      // opens at 10), openStatus returns "Opens at 10 AM" — which
      // contradicts the override-forced open state. In that case use
      // a generic label instead.
      //
      // Fix (Item #13 follow-up polish): previously this used
      //   fromHours.openMeta || 'Open today'
      // which only fell back to the generic label when openMeta was
      // empty. But openMeta is non-empty for the "Opens at 10 AM"
      // case, so the contradictory string was kept and surfaces in
      // the BusinessProfile / BusinessDirectory / ConnectList pill.
      // Now we gate on fromHours.open instead of just truthiness.
      const fromHours = openStatus(biz.hours || {});
      return {
        open: true,
        openMeta: fromHours.open ? fromHours.openMeta : 'Open today',
      };
    }
    return { open: false, openMeta: 'Closed today' };
  }

  // No override active for today — pure hours-based status.
  return openStatus(biz.hours || {});
}
